import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { AlertTriangle, RefreshCcw, X } from 'lucide-react';
import { toast } from 'sonner';

const fmtTime = (s) => {
  if (!s) return '';
  try { return new Date(s).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return s; }
};

/**
 * Phase 7 — Blocked Jobs banner.
 * Polls /api/ai/v2/jobs/blocked, lists each job with the reason and an
 * Unblock button. Auto-hides when the list is empty.
 */
export default function BlockedJobsBanner({ onChange }) {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/ai/v2/jobs/blocked', { credentials: 'include' });
      if (res.ok) {
        const d = await res.json();
        setJobs(d.jobs || []);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
  }, [refresh]);

  const unblock = async (job) => {
    const res = await fetch(`/api/ai/v2/jobs/${job.job_id}/unblock`, {
      method: 'POST', credentials: 'include',
    });
    if (res.ok) {
      toast.success(`Unblocked: ${job.name || job.job_type || job.job_id}`);
      setJobs(prev => prev.filter(j => j.job_id !== job.job_id));
      onChange?.();
    } else {
      toast.error('Unblock failed');
    }
  };

  if (!loading && jobs.length === 0) return null;
  if (jobs.length === 0) return null;

  return (
    <div className="mb-3 rounded-lg border border-red-500/30 bg-red-500/5" data-testid="blocked-jobs-banner">
      <div className="flex items-center gap-2 px-3 py-2 border-b border-red-500/20">
        <AlertTriangle className="w-4 h-4 text-red-500" />
        <span className="text-sm font-medium text-red-600">
          {jobs.length} job{jobs.length === 1 ? '' : 's'} auto-blocked
        </span>
        <span className="text-xs text-muted-foreground">
          (after repeated failures — won't auto-run until cleared)
        </span>
        <Button size="icon" variant="ghost" className="ml-auto h-6 w-6" onClick={refresh} title="Refresh">
          <RefreshCcw className="w-3.5 h-3.5" />
        </Button>
      </div>
      <div className="divide-y divide-red-500/10">
        {jobs.map(j => (
          <div key={j.job_id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`blocked-job-${j.job_id}`}>
            <Badge variant="outline" className="text-[10px] font-mono">{j.job_id}</Badge>
            <span className="font-medium">{j.name || j.job_type || 'Job'}</span>
            <span className="text-xs text-muted-foreground truncate flex-1">
              {j.blocked_reason || 'blocked'} · {fmtTime(j.blocked_at)}
            </span>
            <Button size="sm" variant="outline" className="h-7 text-xs gap-1"
              onClick={() => unblock(j)} data-testid={`unblock-btn-${j.job_id}`}>
              <X className="w-3 h-3" /> Unblock
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}
